import type { ProductionStep } from "./ProductionShell";

/**
 * Three phases over the existing steps (#101): Intake › Storyboard › Release.
 * Step ids and routes stay; this only groups them and owns the next-step wording.
 */
export type PhaseId = "intake" | "storyboard" | "release";
export interface PhaseStep extends ProductionStep {
  accessible: boolean;
  ready: boolean;
  blockedReason: string;
}
export interface PhaseFlags {
  analysisReady: boolean;
  picturesReady: boolean;
  lyricsVisible: boolean;
  lyricsReady: boolean;
  filmMade: boolean;
  photoSession: boolean;
  photoAllSing: boolean;
}
export interface ProductionPhase {
  id: PhaseId;
  label: "Intake" | "Storyboard" | "Release";
  state: "current" | "complete" | "open" | "locked";
  done: boolean;
  steps: PhaseStep[];
  target: string;
  reason: string;
}

const PHASES: { id: PhaseId; label: ProductionPhase["label"]; steps: string[] }[] = [
  { id: "intake", label: "Intake", steps: ["project"] },
  { id: "storyboard", label: "Storyboard", steps: ["visual", "background", "storyboard", "lyrics"] },
  { id: "release", label: "Release", steps: ["render"] },
];

/** Every "go on" button says where it goes and what the maker does there. */
export const NEXT_LABELS = {
  project: "Next: Storyboard — choose your cast & looks",
  visual: "Next: Background — approve each location",
  backgroundBuild: "Next: Storyboard — build the shot plan",
  backgroundReview: "Next: Storyboard — review the shots",
  lyrics: "Next: Lyrics — style the words on screen",
  release: "Storyboard complete — open Release",
} as const;

export function phasesDone(flags: PhaseFlags): Record<PhaseId, boolean> {
  return {
    intake: flags.analysisReady,
    // Sing with your photo has no pictures to make: it is done when every shot sings.
    storyboard: flags.photoSession
      ? flags.photoAllSing
      : flags.picturesReady && (!flags.lyricsVisible || flags.lyricsReady),
    release: flags.filmMade,
  };
}

export function productionPhases(steps: PhaseStep[], done: Record<PhaseId, boolean>): ProductionPhase[] {
  const visible = steps.filter((step) => step.state !== "skipped");
  return PHASES.map(({ id, label, steps: ids }) => {
    const own = visible.filter((step) => ids.includes(step.id));
    // A blocked step is still the one the maker has open.
    const current = own.some((step) => step.state === "current" || step.state === "blocked");
    const state = current ? "current" : done[id] ? "complete" : own.some((step) => step.accessible) ? "open" : "locked";
    const target = own.find((step) => !step.ready && step.accessible) ?? own[own.length - 1];
    return {
      id, label, state, done: done[id], steps: own,
      target: target?.id ?? ids[0]!,
      reason: state === "locked" ? (own[0]?.blockedReason ?? "") : "",
    };
  });
}

export function nextStepAction(
  steps: { id: string }[],
  current: string,
  options: { hasShotPlan?: boolean } = {},
): { label: string; target: string } | null {
  const release = { target: "render", label: NEXT_LABELS.release };
  switch (current) {
    case "project":
      return { target: "visual", label: NEXT_LABELS.project };
    case "visual":
      return { target: "background", label: NEXT_LABELS.visual };
    case "background":
      return { target: "storyboard", label: options.hasShotPlan ? NEXT_LABELS.backgroundReview : NEXT_LABELS.backgroundBuild };
    case "storyboard":
      return steps.some((step) => step.id === "lyrics") ? { target: "lyrics", label: NEXT_LABELS.lyrics } : release;
    case "lyrics":
      return release;
    default:
      return null;
  }
}

/** The earlier step to finish first when the open one is blocked. */
export function unblockTarget(steps: PhaseStep[], current: string): PhaseStep | null {
  const visible = steps.filter((step) => step.state !== "skipped");
  const index = visible.findIndex((step) => step.id === current);
  return visible.slice(0, Math.max(0, index)).find((step) => !step.ready) ?? null;
}

export function phaseProgressText(phases: ProductionPhase[]): string {
  const index = Math.max(0, phases.findIndex((phase) => phase.state === "current"));
  const phase = phases[index]!;
  const head = `Phase ${index + 1} of ${phases.length} · ${phase.label}`;
  if (phase.steps.length < 2) return head;
  const at = Math.max(0, phase.steps.findIndex((step) => step.state === "current" || step.state === "blocked"));
  return `${head} — step ${at + 1} of ${phase.steps.length} · ${phase.steps[at]!.label}`;
}
